import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Play, CheckCircle2, Layers, Palette, TrendingUp, Settings, MapPin, Brain, Satellite, Database, Code, Users, Globe, Shield, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { FeaturedCaseStudies, PlatformsSection } from './HomeComponents';
import { EngineSection, Footer } from './HomeFooter';
import Header from '../components/Header';
import CounterAnimation from '../components/CounterAnimation';

const Home = () => {
  const [scrollY, setScrollY] = useState(0);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="bg-[#0a0a0b] text-white overflow-hidden">
      <Header />
      <HeroSection scrollY={scrollY} />
      <TrustStrip />
      <ImpactMetrics />
      <FeaturedCaseStudies />
      <PlatformsSection />
      <EngineSection />
      <Footer />
    </div>
  );
};

// Hero Section
const HeroSection = ({ scrollY }) => {
  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Video with Parallax */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ scale }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0b]/50 to-[#0a0a0b] z-10" />
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-75"
        >
          <source src="/HERO_REEL_13_1.mp4" type="video/mp4" />
        </video>
      </motion.div>

      {/* Animated Grid Overlay */}
      <div className="absolute inset-0 z-10 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '100px 100px'
        }} />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 z-20 text-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <motion.div
            className="inline-flex items-center space-x-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-4 py-2 mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Satellite className="w-4 h-4 text-cyan-400" />
            <span className="text-sm text-gray-300">Trusted by Governments & Global Enterprises</span>
          </motion.div>

          <h1 className="text-6xl md:text-8xl font-bold mb-6 leading-tight tracking-tight">
            Thinking Like Founders.
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Building Like Engineers.
            </span>
          </h1>

          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
            A global team of innovators, engineers, and strategists united by one mission: building
            technology that transforms nations.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              className="bg-white text-black hover:bg-gray-200 px-8 py-6 text-lg group transition-all duration-300 hover:scale-105"
              onClick={() => window.dispatchEvent(new CustomEvent('open-chat-widget'))}
            >
              Talk to an Expert
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Link to="/work">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 hover:text-white/50 px-8 py-6 text-lg backdrop-blur-sm transition-all duration-300">
                View Our Work
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Trust Strip with Marquee
const TrustStrip = () => {
  const clients = [
    { name: 'Esri UK', logo: 'Esri UK' },
    { name: 'Intellecap', logo: 'Intellecap' },
    { name: 'Aavishkar Foundation', logo: 'Aavishkar Foundation' },
    { name: 'SatSure', logo: 'SatSure' },
    { name: 'Maps.Guru', logo: 'Maps.Guru' },
    { name: 'GeoSpoc', logo: 'GeoSpoc' },
  ];

  return (
    <section className="py-16 border-y border-white/5 bg-[#0f0f10] relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0b] via-transparent to-[#0a0a0b] z-10 pointer-events-none" />

      <div className="flex animate-marquee gap-9">
        {[...clients, ...clients, ...clients].map((client, index) => (
          <div
            key={`${client.name}-${index}`}
            className="flex-shrink-0 px-4 py-2 bg-white/5 rounded-lg border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all duration-300 cursor-pointer group"
          >
            <span className="text-2xl text-gray-400 group-hover:text-white transition-colors whitespace-nowrap">
              {client.logo}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
};

// Impact Metrics with Counter Animation
const ImpactMetrics = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const metrics = [
    { value: 100, suffix: '', label: 'Users Onboarded', description: 'On maps.guru in early access', isText: false },
    { value: 10000, suffix: '', label: 'Tile Loads in Beta', description: 'Map tiles served during beta', isText: false },
    { display: 'Days → Seconds', label: 'Analytics Turnaround', description: 'From days to seconds', isText: true },
  ];

  return (
    <section ref={ref} className="py-32 bg-gradient-to-b from-[#0a0a0b] to-[#0f0f10]">
      <div className="container mx-auto px-6">
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-5xl md:text-6xl font-bold mb-6">Impact at Scale</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Delivering measurable outcomes for governments and enterprises
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              className="relative p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 hover:border-white/20 transition-all duration-500 group"
              initial={{ opacity: 0, y: 50 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative">
                <div className="text-5xl font-bold mb-3 bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {metric.isText
                    ? <span>{metric.display}</span>
                    : <CounterAnimation end={metric.value} suffix={metric.suffix} prefix={metric.prefix || ''} />
                  }
                </div>
                <h3 className="text-xl font-semibold mb-2">{metric.label}</h3>
                <p className="text-sm text-gray-400">{metric.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Home;
